import { KrCard } from '@/common/card/KrCard';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { far } from '@awesome.me/kit-b4fd369ea4/icons';
import { useEffect, useMemo, useState } from 'react';

export const Info = () => {
  const module = useModuleFromRoute();
  const rsrc = useRsrc();

  const [info, setInfo] = useState(null);

  useEffect(() => {
    rsrc.info(module).then((data) => {
      setInfo(data);
    });
  }, [module, rsrc]);

  const formatValue = (value) => {
    if (value == null) return '—';
    if (typeof value === 'number') return value.toLocaleString();
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (Array.isArray(value)) return value.map((item) => formatValue(item)).join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const formatDateTimeLocal = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleString();
  };

  const formatKeyLabel = (key) => {
    const acronyms = new Set(['id', 'url', 'uri', 'os', 'ip', 'sha', 'md5', 'cpu', 'gpu']);
    const words = key
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[._-]/g, ' ')
      .trim()
      .split(/\s+/);

    return (
      words
        .map((word) => {
          const lower = word.toLowerCase();
          if (acronyms.has(lower)) return lower.toUpperCase();
          return lower.charAt(0).toUpperCase() + lower.slice(1);
        })
        .join(' ') + ' Details'
    );
  };

  const formatDetailValue = (value, key) => {
    if (typeof value === 'string' && key.toLowerCase().includes('time')) {
      return formatDateTimeLocal(value);
    }
    return formatValue(value);
  };

  const getValue = (obj, path, fallback = '—') => {
    const result = path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
    return result == null ? fallback : result;
  };

  const getFirstValue = (obj, paths, fallback = '—') => {
    for (const path of paths) {
      const value = getValue(obj, path, null);
      if (value != null) {
        return value;
      }
    }
    return fallback;
  };

  const flattenObject = (obj, prefix = '') => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      return [{ key: prefix || 'value', value: obj }];
    }

    return Object.entries(obj).flatMap(([key, value]) => {
      const path = prefix ? `${prefix}.${key}` : key;
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return flattenObject(value, path);
      }
      return [{ key: path, value }];
    });
  };

  const summaryCards = useMemo(() => {
    if (!info) return [];
    const git = info.git || {};
    const build = git.build || info.build || {};
    const version = getFirstValue(info, ['git.build.version', 'build.version', 'app.version', 'info.project.version']);
    const buildTime = getFirstValue(info, ['git.build.time', 'build.time']);
    const commitShort = getFirstValue(info, ['git.commit.id.describe-short', 'git.commit.id.abbrev']);
    const commitFull = getFirstValue(info, ['git.commit.id.full']);
    const branch = getValue(git, 'branch');
    const dirty = getValue(git, 'dirty');

    return [
      {
        key: 'summary',
        title: 'Build Summary',
        rows: [
          { key: 'version', label: 'Version', value: version },
          { key: 'buildTime', label: 'Build Time', value: formatDateTimeLocal(buildTime) },
          { key: 'commitShort', label: 'Commit', value: commitShort },
          { key: 'branch', label: 'Branch', value: branch },
          { key: 'dirty', label: 'Dirty', value: dirty },
          { key: 'buildHost', label: 'Build Host', value: getValue(build, 'host') },
          { key: 'buildUser', label: 'Build User', value: getValue(build, 'user.name') },
          { key: 'buildEmail', label: 'Build Email', value: getValue(build, 'user.email') },
        ],
      },
      {
        key: 'commit',
        title: 'Commit Summary',
        rows: [
          { key: 'message', label: 'Message', value: getValue(git, 'commit.message.short') },
          { key: 'commitFull', label: 'Commit SHA', value: commitFull },
          { key: 'author', label: 'Author', value: getValue(git, 'commit.user.name') },
          {
            key: 'commitTime',
            label: 'Commit Time',
            value: formatDateTimeLocal(getValue(git, 'commit.committer.time')),
          },
        ],
      },
    ];
  }, [info]);

  const detailCards = useMemo(() => {
    if (!info) return [];
    const entries = Object.entries(info)
      .filter(([key]) => key.toLowerCase() !== 'git')
      .map(([key, value]) => ({
        key,
        title: formatKeyLabel(key),
        rows: flattenObject(value).map((row) => ({
          key: row.key,
          label: formatKeyLabel(row.key),
          value: formatDetailValue(row.value, row.key),
        })),
      }))
      .sort((a, b) => a.title.localeCompare(b.title));

    return entries;
  }, [info]);

  const pageStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  };

  const contentStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingRight: '4px',
  };

  const dashboardGridStyle = {
    columnWidth: '520px',
    columnGap: '16px',
    columnFill: 'balance',
  };

  const rowsStyle = {
    display: 'grid',
    gridTemplateColumns: 'minmax(140px, 1fr) minmax(180px, 2fr)',
    rowGap: '10px',
    columnGap: '12px',
    alignItems: 'center',
  };

  const keyStyle = {
    fontSize: '12px',
    fontWeight: 600,
    color: 'inherit',
    opacity: 0.7,
    textTransform: 'none',
    wordBreak: 'break-word',
  };

  const valueStyle = {
    fontSize: '13px',
    fontWeight: 500,
    wordBreak: 'break-word',
  };

  const cardWrapperStyle = {
    minWidth: 0,
    width: '100%',
    breakInside: 'avoid',
    display: 'inline-block',
    marginBottom: '16px',
  };

  const renderCard = (card) => (
    <div key={card.key} style={cardWrapperStyle}>
      <KrCard title={card.title}>
        <div style={rowsStyle}>
          {card.rows.map((row) => (
            <div key={`${card.key}-${row.key}-row`} style={{ display: 'contents' }}>
              <div style={keyStyle}>{row.label}</div>
              <div style={valueStyle}>{row.value}</div>
            </div>
          ))}
        </div>
      </KrCard>
    </div>
  );

  const title = (module ? module.charAt(0).toUpperCase() + module.slice(1) : 'Module') + ' System Info';

  if (!info) return null;

  return (
    <div style={pageStyle}>
      <Title icon={far.faInfoCircle} title={title} />
      <div style={contentStyle}>
        <div style={dashboardGridStyle}>
          {summaryCards.map(renderCard)}
          {detailCards.map(renderCard)}
        </div>
      </div>
    </div>
  );
};
